import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Share2, Copy, Users, Calendar, Edit2,
  Trash2, X, Check, ExternalLink, User, MapPin, Search,
  FolderKanban, CheckCircle, XCircle, Clock, Link2, Video,
  SquareCheck, Square, Image, FileText, Globe, Upload
} from "lucide-react";
import { compressImages, formatFileSize } from "../utils/imageCompression";
import { getPublicUrlAsync, getProjectShareUrlAsync, getProjectShareUrl, safeCopyToClipboard } from "../utils/shareHelper";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CLIENT_STATUS_COLORS = {
  Pending: "bg-gray-100 text-gray-800",
  Selected: "bg-green-100 text-green-800",
  Rejected: "bg-red-100 text-red-800"
};

const MEETING_TYPES = [
  { value: "Audition", label: "Audition" },
  { value: "Meeting", label: "Meeting" },
  { value: "Call", label: "Call" },
  { value: "Site Visit", label: "Site Visit" }
];

const OPTION_TYPES = [
  { value: "profile", label: "Profile", icon: User, description: "From TalentBoard" },
  { value: "image_gallery", label: "Images", icon: Image, description: "Up to 8 photos" },
  { value: "pdf", label: "PDF", icon: FileText, description: "Single document" },
  { value: "external_link", label: "Link", icon: Globe, description: "External URL" }
];

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Modals
  const [showAddRoleModal, setShowAddRoleModal] = useState(false);
  const [showAddOptionModal, setShowAddOptionModal] = useState(null); // roleId
  const [showShareModal, setShowShareModal] = useState(false);
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(null);
  
  // Option type selection
  const [selectedOptionType, setSelectedOptionType] = useState("profile");
  
  // Form states
  const [roleForm, setRoleForm] = useState({ role_name: "", required_count: "" });
  const [editForm, setEditForm] = useState({ title: "", description: "", deadline: "" });
  const [meetingForm, setMeetingForm] = useState({
    meeting_date: "", meeting_time: "", meeting_link: "", meeting_type: "Meeting"
  });
  
  // Option forms
  const [imageGalleryForm, setImageGalleryForm] = useState({ title: "", description: "", files: [] });
  const [pdfForm, setPdfForm] = useState({ title: "", description: "", file: null });
  const [linkForm, setLinkForm] = useState({ title: "", description: "", external_url: "" });
  
  // File refs
  const imageInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  
  // TalentBoard for adding profiles
  const [talentBoard, setTalentBoard] = useState([]);
  const [talentSearch, setTalentSearch] = useState("");
  const [selectedForAdd, setSelectedForAdd] = useState([]);
  const [loadingTalentBoard, setLoadingTalentBoard] = useState(false);
  
  // Multi-select for bulk actions
  const [selectedCandidates, setSelectedCandidates] = useState({});
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  
  // Upload progress
  const [uploading, setUploading] = useState(false);

  const fetchProject = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setProject(data);
        setEditForm({
          title: data.title || "",
          description: data.description || "",
          deadline: data.deadline || ""
        });
        setSelectedCandidates({});
      } else {
        toast.error("Failed to load project");
        navigate("/enterprise/projects");
      }
    } catch (error) {
      console.error("Error fetching project:", error);
      toast.error("Failed to load project");
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  const fetchTalentBoard = async (search = "") => {
    setLoadingTalentBoard(true);
    try {
      const token = localStorage.getItem("token");
      const url = search 
        ? `${API_URL}/api/talentboard/list?search=${encodeURIComponent(search)}`
        : `${API_URL}/api/talentboard/list`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setTalentBoard(data.entries || []);
      }
    } catch (error) {
      console.error("Error fetching TalentBoard:", error);
    } finally {
      setLoadingTalentBoard(false);
    }
  };

  // Role actions
  const handleCreateRole = async () => {
    if (!roleForm.role_name.trim()) {
      toast.error("Requirement name is required");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/projects/${projectId}/roles`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          role_name: roleForm.role_name,
          required_count: roleForm.required_count ? parseInt(roleForm.required_count) : null
        })
      });

      if (response.ok) {
        toast.success("Requirement created!");
        setShowAddRoleModal(false);
        setRoleForm({ role_name: "", required_count: "" });
        fetchProject();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to create requirement");
      }
    } catch (error) {
      toast.error("Failed to create requirement");
    }
  };

  const handleDeleteRole = async (roleId, roleName) => {
    if (!window.confirm(`Delete "${roleName}" and all its options?`)) return;
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/roles/${roleId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success("Requirement deleted");
        fetchProject();
      } else {
        toast.error("Failed to delete requirement");
      }
    } catch (error) {
      toast.error("Failed to delete requirement");
    }
  };

  // Open add option modal
  const openAddOptionModal = (roleId) => {
    setShowAddOptionModal(roleId);
    setSelectedOptionType("profile");
    setSelectedForAdd([]);
    setTalentSearch("");
    setImageGalleryForm({ title: "", description: "", files: [] });
    setPdfForm({ title: "", description: "", file: null });
    setLinkForm({ title: "", description: "", external_url: "" });
    fetchTalentBoard();
  };

  // Add profile options
  const handleAddProfiles = async () => {
    if (selectedForAdd.length === 0) {
      toast.error("Select at least one profile");
      return;
    }

    const token = localStorage.getItem("token");
    let added = 0;
    let failed = 0;

    for (const profileId of selectedForAdd) {
      try {
        const response = await fetch(`${API_URL}/api/roles/${showAddOptionModal}/options`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ 
            option_type: "profile",
            profile_id: profileId 
          })
        });

        if (response.ok) {
          added++;
        } else {
          failed++;
        }
      } catch {
        failed++;
      }
    }

    if (added > 0) toast.success(`Added ${added} profile${added > 1 ? 's' : ''}`);
    if (failed > 0) toast.error(`${failed} already in requirement or failed`);

    setShowAddOptionModal(null);
    fetchProject();
  };

  // Upload and add image gallery
  const handleAddImageGallery = async () => {
    if (!imageGalleryForm.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (imageGalleryForm.files.length === 0) {
      toast.error("Select at least one image");
      return;
    }
    if (imageGalleryForm.files.length > 8) {
      toast.error("Maximum 8 images allowed");
      return;
    }

    setUploading(true);
    try {
      // Compress images
      toast.info("Compressing images...");
      const compressedFiles = await compressImages(imageGalleryForm.files, 5);
      
      // Upload images
      toast.info("Uploading images...");
      const token = localStorage.getItem("token");
      const formData = new FormData();
      compressedFiles.forEach(file => formData.append("files", file));
      
      const uploadResponse = await fetch(`${API_URL}/api/upload/images`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (!uploadResponse.ok) {
        const err = await uploadResponse.json();
        throw new Error(err.detail || "Upload failed");
      }

      const { urls } = await uploadResponse.json();

      // Add option
      const response = await fetch(`${API_URL}/api/roles/${showAddOptionModal}/options`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          option_type: "image_gallery",
          title: imageGalleryForm.title,
          description: imageGalleryForm.description,
          images: urls
        })
      });

      if (response.ok) {
        toast.success("Image gallery added!");
        setShowAddOptionModal(null);
        fetchProject();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to add gallery");
      }
    } catch (error) {
      toast.error(error.message || "Failed to upload images");
    } finally {
      setUploading(false);
    }
  };

  // Upload and add PDF
  const handleAddPdf = async () => {
    if (!pdfForm.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!pdfForm.file) {
      toast.error("Select a PDF file");
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", pdfForm.file);
      
      const uploadResponse = await fetch(`${API_URL}/api/upload/pdf`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (!uploadResponse.ok) {
        const err = await uploadResponse.json();
        throw new Error(err.detail || "Upload failed");
      }

      const { url } = await uploadResponse.json();

      // Add option
      const response = await fetch(`${API_URL}/api/roles/${showAddOptionModal}/options`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          option_type: "pdf",
          title: pdfForm.title,
          description: pdfForm.description,
          pdf_url: url
        })
      });

      if (response.ok) {
        toast.success("PDF added!");
        setShowAddOptionModal(null);
        fetchProject();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to add PDF");
      }
    } catch (error) {
      toast.error(error.message || "Failed to upload PDF");
    } finally {
      setUploading(false);
    }
  };

  // Add external link
  const handleAddLink = async () => {
    if (!linkForm.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!linkForm.external_url.trim()) {
      toast.error("URL is required");
      return;
    }

    // Basic URL validation
    try {
      new URL(linkForm.external_url);
    } catch {
      toast.error("Please enter a valid URL");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/roles/${showAddOptionModal}/options`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          option_type: "external_link",
          title: linkForm.title,
          description: linkForm.description,
          external_url: linkForm.external_url
        })
      });

      if (response.ok) {
        toast.success("Link added!");
        setShowAddOptionModal(null);
        fetchProject();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to add link");
      }
    } catch (error) {
      toast.error("Failed to add link");
    }
  };

  const handleRemoveOption = async (optionId, name) => {
    if (!window.confirm(`Remove "${name}" from this requirement?`)) return;
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/candidates/${optionId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success("Option removed");
        fetchProject();
      } else {
        toast.error("Failed to remove option");
      }
    } catch (error) {
      toast.error("Failed to remove option");
    }
  };

  // Meeting scheduling
  const openMeetingModal = (candidate) => {
    setShowMeetingModal(candidate);
    setMeetingForm({
      meeting_date: candidate.meeting_date || "",
      meeting_time: candidate.meeting_time || "",
      meeting_link: candidate.meeting_link || "",
      meeting_type: candidate.meeting_type || "Meeting"
    });
  };

  const handleSaveMeeting = async () => {
    if (!meetingForm.meeting_date) {
      toast.error("Please select a date");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/candidates/${showMeetingModal.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(meetingForm)
      });

      if (response.ok) {
        toast.success("Meeting scheduled!");
        setShowMeetingModal(null);
        fetchProject();
      } else {
        toast.error("Failed to save meeting");
      }
    } catch (error) {
      toast.error("Failed to save meeting");
    }
  };

  // Bulk actions
  const toggleCandidateSelection = (candidateId) => {
    setSelectedCandidates(prev => ({
      ...prev,
      [candidateId]: !prev[candidateId]
    }));
  };

  const selectAllInRole = (roleId) => {
    const role = project?.roles?.find(r => r.id === roleId);
    if (!role) return;
    
    const allSelected = role.candidates?.every(c => selectedCandidates[c.id]);
    const updates = {};
    role.candidates?.forEach(c => {
      updates[c.id] = !allSelected;
    });
    setSelectedCandidates(prev => ({ ...prev, ...updates }));
  };

  const getSelectedCount = () => {
    return Object.values(selectedCandidates).filter(Boolean).length;
  };

  const handleBulkAction = async (action) => {
    const ids = Object.entries(selectedCandidates)
      .filter(([_, selected]) => selected)
      .map(([id]) => id);
    
    if (ids.length === 0) {
      toast.error("No options selected");
      return;
    }

    setBulkActionLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/candidates/bulk`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          candidate_ids: ids,
          action: action
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setSelectedCandidates({});
        fetchProject();
      } else {
        toast.error("Bulk action failed");
      }
    } catch (error) {
      toast.error("Bulk action failed");
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Share link
  const handleCreateShareLink = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/projects/${projectId}/share`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success("Share link created!");
        fetchProject();
        setShowShareModal(true);
      } else {
        toast.error("Failed to create share link");
      }
    } catch (error) {
      toast.error("Failed to create share link");
    }
  };

  const copyShareLink = async () => {
    if (!project?.share_link?.token) return;
    // Use OG-enabled share URL for link previews on WhatsApp/Telegram
    const url = await getProjectShareUrlAsync(project.share_link.token);
    const success = await safeCopyToClipboard(url);
    if (success) {
      toast.success("Link copied!");
    } else {
      toast.info("Copy this URL: " + url, { duration: 10000 });
    }
  };

  // Update project
  const handleUpdateProject = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/projects/${projectId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        toast.success("Project updated!");
        setShowEditProjectModal(false);
        fetchProject();
      } else {
        toast.error("Failed to update project");
      }
    } catch (error) {
      toast.error("Failed to update project");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", {
        day: "numeric", month: "short", year: "numeric"
      });
    } catch {
      return dateStr;
    }
  };

  const getExistingProfileIds = (roleId) => {
    const role = project?.roles?.find(r => r.id === roleId);
    return role?.candidates?.filter(c => c.option_type === "profile" || !c.option_type).map(c => c.profile_id) || [];
  };

  const getOptionDisplayName = (option) => {
    if (option.option_type === "profile" || !option.option_type) {
      return option.profile_name || "Unknown Profile";
    }
    return option.title || "Untitled";
  };

  // Render option card based on type
  const renderOptionCard = (option, role) => {
    const optionType = option.option_type || "profile";
    const isSelected = selectedCandidates[option.id];
    
    return (
      <div 
        key={option.id}
        className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
          isSelected ? "bg-blue-50 border-blue-300" : "bg-gray-50"
        }`}
        data-testid={`option-card-${option.id}`}
      >
        {/* Checkbox */}
        <button onClick={() => toggleCandidateSelection(option.id)} className="flex-shrink-0 mt-1">
          {isSelected ? <SquareCheck className="w-5 h-5 text-blue-600" /> : <Square className="w-5 h-5 text-gray-400" />}
        </button>
        
        {/* Content based on type */}
        {optionType === "profile" || !optionType ? (
          // Profile card
          <>
            <div className="w-12 h-12 rounded-lg bg-gray-200 overflow-hidden flex-shrink-0">
              {option.profile_photo ? (
                <img src={option.profile_photo} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User className="w-5 h-5 text-gray-400" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <a href={`/p/${option.profile_slug}`} target="_blank" rel="noopener noreferrer"
                  className="font-medium text-gray-900 hover:text-blue-600 flex items-center gap-1 truncate">
                  {option.profile_name} <ExternalLink className="w-3 h-3 flex-shrink-0" />
                </a>
                <Button variant="ghost" size="sm" onClick={() => handleRemoveOption(option.id, option.profile_name)}
                  className="h-6 w-6 p-0 text-gray-400 hover:text-red-500">
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {option.profile_category}{option.profile_location && ` • ${option.profile_location}`}
              </div>
              <Badge className={`mt-1.5 text-xs ${CLIENT_STATUS_COLORS[option.client_status]}`}>
                {option.client_status === "Selected" && <CheckCircle className="w-3 h-3 mr-1" />}
                {option.client_status === "Rejected" && <XCircle className="w-3 h-3 mr-1" />}
                {option.client_status === "Pending" && <Clock className="w-3 h-3 mr-1" />}
                {option.client_status}
              </Badge>
              {option.meeting_date && (
                <div className="mt-2 text-xs text-blue-600 flex items-center gap-1">
                  <Video className="w-3 h-3" />
                  {option.meeting_type || "Meeting"}: {formatDate(option.meeting_date)}
                  {option.meeting_time && ` at ${option.meeting_time}`}
                </div>
              )}
              <Button variant="ghost" size="sm" onClick={() => openMeetingModal(option)}
                className="mt-1 h-7 text-xs text-gray-500 hover:text-blue-600 p-0">
                <Calendar className="w-3 h-3 mr-1" />
                {option.meeting_date ? "Edit Meeting" : "Schedule Meeting"}
              </Button>
            </div>
          </>
        ) : optionType === "image_gallery" ? (
          // Image gallery card
          <>
            <div className="w-12 h-12 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
              <Image className="w-5 h-5 text-violet-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <span className="font-medium text-gray-900 truncate">{option.title}</span>
                <Button variant="ghost" size="sm" onClick={() => handleRemoveOption(option.id, option.title)}
                  className="h-6 w-6 p-0 text-gray-400 hover:text-red-500">
                  <X className="w-4 h-4" />
                </Button>
              </div>
              {option.description && <p className="text-xs text-gray-500 mt-0.5">{option.description}</p>}
              <div className="flex gap-1 mt-2 flex-wrap">
                {option.images?.slice(0, 4).map((img, i) => (
                  <img key={i} src={`${API_URL}${img}`} alt="" className="w-10 h-10 rounded object-cover" />
                ))}
                {option.images?.length > 4 && (
                  <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center text-xs text-gray-600">
                    +{option.images.length - 4}
                  </div>
                )}
              </div>
              <Badge className={`mt-2 text-xs ${CLIENT_STATUS_COLORS[option.client_status]}`}>
                {option.client_status}
              </Badge>
            </div>
          </>
        ) : optionType === "pdf" ? (
          // PDF card
          <>
            <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <span className="font-medium text-gray-900 truncate">{option.title}</span>
                <Button variant="ghost" size="sm" onClick={() => handleRemoveOption(option.id, option.title)}
                  className="h-6 w-6 p-0 text-gray-400 hover:text-red-500">
                  <X className="w-4 h-4" />
                </Button>
              </div>
              {option.description && <p className="text-xs text-gray-500 mt-0.5">{option.description}</p>}
              <a href={`${API_URL}${option.pdf_url}`} target="_blank" rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-2">
                <FileText className="w-3 h-3" /> View PDF
              </a>
              <Badge className={`mt-2 text-xs ${CLIENT_STATUS_COLORS[option.client_status]}`}>
                {option.client_status}
              </Badge>
            </div>
          </>
        ) : optionType === "external_link" ? (
          // External link card
          <>
            <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
              <Globe className="w-5 h-5 text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <span className="font-medium text-gray-900 truncate">{option.title}</span>
                <Button variant="ghost" size="sm" onClick={() => handleRemoveOption(option.id, option.title)}
                  className="h-6 w-6 p-0 text-gray-400 hover:text-red-500">
                  <X className="w-4 h-4" />
                </Button>
              </div>
              {option.description && <p className="text-xs text-gray-500 mt-0.5">{option.description}</p>}
              <a href={option.external_url} target="_blank" rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-2 truncate">
                <ExternalLink className="w-3 h-3" /> {option.external_url}
              </a>
              <Badge className={`mt-2 text-xs ${CLIENT_STATUS_COLORS[option.client_status]}`}>
                {option.client_status}
              </Badge>
            </div>
          </>
        ) : null}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!project) return null;

  const totalOptions = project.roles?.reduce((sum, r) => sum + (r.candidates?.length || 0), 0) || 0;
  const selectedCount = project.roles?.reduce((sum, r) => 
    sum + (r.candidates?.filter(c => c.client_status === "Selected").length || 0), 0) || 0;
  const bulkSelectedCount = getSelectedCount();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/enterprise/projects")} data-testid="project-back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Projects
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold text-gray-900">{project.title}</h1>
                  <Button variant="ghost" size="sm" onClick={() => setShowEditProjectModal(true)}>
                    <Edit2 className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-500">
                  <span>{project.roles?.length || 0} requirements</span>
                  <span>{totalOptions} options</span>
                  {selectedCount > 0 && (
                    <Badge className="bg-green-100 text-green-800">
                      <CheckCircle className="w-3 h-3 mr-1" /> {selectedCount} selected
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => project.share_link ? setShowShareModal(true) : handleCreateShareLink()}
                data-testid="share-project-btn">
                <Share2 className="w-4 h-4 mr-2" />
                {project.share_link ? "Share Link" : "Generate Link"}
              </Button>
              <Button onClick={() => setShowAddRoleModal(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="add-role-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Requirement
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Bulk Actions Bar */}
      {bulkSelectedCount > 0 && (
        <div className="bg-blue-600 text-white sticky top-[73px] z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{bulkSelectedCount} option{bulkSelectedCount > 1 ? 's' : ''} selected</span>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="secondary" onClick={() => handleBulkAction("select")} disabled={bulkActionLoading}>
                  <CheckCircle className="w-4 h-4 mr-1" /> Mark Selected
                </Button>
                <Button size="sm" variant="secondary" onClick={() => handleBulkAction("deselect")} disabled={bulkActionLoading}>
                  <Clock className="w-4 h-4 mr-1" /> Mark Pending
                </Button>
                <Button size="sm" variant="secondary" className="text-red-600" onClick={() => handleBulkAction("remove")} disabled={bulkActionLoading}>
                  <Trash2 className="w-4 h-4 mr-1" /> Remove
                </Button>
                <Button size="sm" variant="ghost" className="text-white hover:bg-blue-700" onClick={() => setSelectedCandidates({})}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Project Info */}
      {(project.description || project.deadline) && (
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center gap-6 text-sm">
              {project.description && <p className="text-gray-600">{project.description}</p>}
              {project.deadline && (
                <span className="flex items-center gap-1 text-orange-600">
                  <Calendar className="w-4 h-4" /> Deadline: {formatDate(project.deadline)}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content - Requirements */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {project.roles?.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <FolderKanban className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No requirements yet</h3>
              <p className="text-gray-500 mb-4">Add requirements to organize options for different needs.</p>
              <Button onClick={() => setShowAddRoleModal(true)} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" /> Add First Requirement
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {project.roles.map((role) => (
              <Card key={role.id} data-testid={`role-card-${role.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CardTitle className="text-lg">{role.role_name}</CardTitle>
                      {role.required_count && <Badge variant="outline">Need {role.required_count}</Badge>}
                      <Badge variant="secondary">{role.candidates?.length || 0} options</Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      {role.candidates?.length > 0 && (
                        <Button variant="ghost" size="sm" onClick={() => selectAllInRole(role.id)} className="text-gray-500">
                          {role.candidates?.every(c => selectedCandidates[c.id]) 
                            ? <SquareCheck className="w-4 h-4 mr-1" /> : <Square className="w-4 h-4 mr-1" />}
                          Select All
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => openAddOptionModal(role.id)} data-testid={`add-option-btn-${role.id}`}>
                        <Plus className="w-4 h-4 mr-1" /> Add Option
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteRole(role.id, role.role_name)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {role.candidates?.length === 0 ? (
                    <div className="text-center py-6 text-gray-500">
                      <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">No options added yet</p>
                      <Button variant="link" size="sm" onClick={() => openAddOptionModal(role.id)}>
                        Add profiles, images, PDFs, or links
                      </Button>
                    </div>
                  ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {role.candidates.map((option) => renderOptionCard(option, role))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Add Requirement Modal */}
      {showAddRoleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg">Add Requirement</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowAddRoleModal(false)}><X className="w-4 h-4" /></Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Requirement Name *</Label>
                <Input placeholder="e.g., Lead Actor, Location Photos, Script" value={roleForm.role_name}
                  onChange={(e) => setRoleForm(prev => ({ ...prev, role_name: e.target.value }))} data-testid="role-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Required Count (optional)</Label>
                <Input type="number" placeholder="e.g., 3" value={roleForm.required_count}
                  onChange={(e) => setRoleForm(prev => ({ ...prev, required_count: e.target.value }))} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowAddRoleModal(false)}>Cancel</Button>
                <Button onClick={handleCreateRole} className="bg-blue-600 hover:bg-blue-700" data-testid="role-create-submit">
                  <Check className="w-4 h-4 mr-2" /> Add Requirement
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add Option Modal */}
      {showAddOptionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[85vh] flex flex-col">
            <CardHeader className="flex flex-row items-center justify-between pb-2 flex-shrink-0">
              <CardTitle className="text-lg">Add Option</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowAddOptionModal(null)}><X className="w-4 h-4" /></Button>
            </CardHeader>
            <CardContent className="flex-1 overflow-hidden flex flex-col">
              {/* Option Type Tabs */}
              <div className="flex gap-2 mb-4 flex-shrink-0 overflow-x-auto pb-2">
                {OPTION_TYPES.map(type => (
                  <button key={type.value}
                    onClick={() => setSelectedOptionType(type.value)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors whitespace-nowrap ${
                      selectedOptionType === type.value ? "bg-blue-50 border-blue-300 text-blue-700" : "hover:bg-gray-50"
                    }`} data-testid={`option-type-${type.value}`}>
                    <type.icon className="w-4 h-4" />
                    <div className="text-left">
                      <div className="text-sm font-medium">{type.label}</div>
                      <div className="text-xs text-gray-500">{type.description}</div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Content based on selected type */}
              <div className="flex-1 overflow-y-auto">
                {selectedOptionType === "profile" && (
                  <>
                    <div className="relative mb-4">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input placeholder="Search TalentBoard..." value={talentSearch}
                        onChange={(e) => { setTalentSearch(e.target.value); fetchTalentBoard(e.target.value); }}
                        className="pl-9" data-testid="profile-search-input" />
                    </div>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {loadingTalentBoard ? (
                        <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div></div>
                      ) : talentBoard.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                          <p>No profiles in TalentBoard</p>
                        </div>
                      ) : (
                        talentBoard.map((entry) => {
                          const isAlreadyAdded = getExistingProfileIds(showAddOptionModal).includes(entry.profile_id);
                          const isSelected = selectedForAdd.includes(entry.profile_id);
                          return (
                            <div key={entry.id}
                              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                isAlreadyAdded ? "bg-gray-100 opacity-50 cursor-not-allowed" : isSelected ? "bg-blue-50 border-blue-300" : "hover:bg-gray-50"
                              }`}
                              onClick={() => { if (!isAlreadyAdded) setSelectedForAdd(prev => isSelected ? prev.filter(id => id !== entry.profile_id) : [...prev, entry.profile_id]); }}>
                              <div className={`w-5 h-5 rounded border flex items-center justify-center ${isSelected ? "bg-blue-600 border-blue-600" : "border-gray-300"}`}>
                                {isSelected && <Check className="w-3 h-3 text-white" />}
                              </div>
                              <div className="w-10 h-10 rounded-lg bg-gray-200 overflow-hidden flex-shrink-0">
                                {entry.profile_photo ? <img src={entry.profile_photo} alt="" className="w-full h-full object-cover" />
                                  : <div className="w-full h-full flex items-center justify-center"><User className="w-4 h-4 text-gray-400" /></div>}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 truncate">{entry.profile_name}</p>
                                <p className="text-xs text-gray-500">{entry.profile_category}{entry.profile_location && ` • ${entry.profile_location}`}</p>
                              </div>
                              {isAlreadyAdded && <Badge variant="secondary" className="text-xs">Added</Badge>}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </>
                )}

                {selectedOptionType === "image_gallery" && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Title *</Label>
                      <Input placeholder="e.g., Location Photos, Costume References" value={imageGalleryForm.title}
                        onChange={(e) => setImageGalleryForm(prev => ({ ...prev, title: e.target.value }))} data-testid="gallery-title-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Description (optional)</Label>
                      <Textarea placeholder="Brief description..." value={imageGalleryForm.description}
                        onChange={(e) => setImageGalleryForm(prev => ({ ...prev, description: e.target.value }))} rows={2} />
                    </div>
                    <div className="space-y-2">
                      <Label>Images (max 8, 5MB each)</Label>
                      <input ref={imageInputRef} type="file" accept="image/*" multiple className="hidden"
                        onChange={(e) => setImageGalleryForm(prev => ({ ...prev, files: Array.from(e.target.files || []).slice(0, 8) }))} />
                      <Button variant="outline" onClick={() => imageInputRef.current?.click()} className="w-full" data-testid="select-images-btn">
                        <Upload className="w-4 h-4 mr-2" /> Select Images
                      </Button>
                      {imageGalleryForm.files.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {imageGalleryForm.files.map((file, i) => (
                            <div key={i} className="relative">
                              <img src={URL.createObjectURL(file)} alt="" className="w-16 h-16 rounded object-cover" />
                              <span className="absolute bottom-0 right-0 bg-black/60 text-white text-xs px-1 rounded">
                                {formatFileSize(file.size)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-gray-500">{imageGalleryForm.files.length}/8 images selected</p>
                    </div>
                  </div>
                )}

                {selectedOptionType === "pdf" && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Title *</Label>
                      <Input placeholder="e.g., Script, Contract, Portfolio" value={pdfForm.title}
                        onChange={(e) => setPdfForm(prev => ({ ...prev, title: e.target.value }))} data-testid="pdf-title-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Description (optional)</Label>
                      <Textarea placeholder="Brief description..." value={pdfForm.description}
                        onChange={(e) => setPdfForm(prev => ({ ...prev, description: e.target.value }))} rows={2} />
                    </div>
                    <div className="space-y-2">
                      <Label>PDF File (max 15MB)</Label>
                      <input ref={pdfInputRef} type="file" accept=".pdf,application/pdf" className="hidden"
                        onChange={(e) => setPdfForm(prev => ({ ...prev, file: e.target.files?.[0] || null }))} />
                      <Button variant="outline" onClick={() => pdfInputRef.current?.click()} className="w-full" data-testid="select-pdf-btn">
                        <Upload className="w-4 h-4 mr-2" /> Select PDF
                      </Button>
                      {pdfForm.file && (
                        <div className="flex items-center gap-2 p-2 bg-gray-50 rounded mt-2">
                          <FileText className="w-5 h-5 text-red-500" />
                          <span className="text-sm truncate flex-1">{pdfForm.file.name}</span>
                          <span className="text-xs text-gray-500">{formatFileSize(pdfForm.file.size)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {selectedOptionType === "external_link" && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Title *</Label>
                      <Input placeholder="e.g., Portfolio Website, Demo Reel" value={linkForm.title}
                        onChange={(e) => setLinkForm(prev => ({ ...prev, title: e.target.value }))} data-testid="link-title-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Description (optional)</Label>
                      <Textarea placeholder="Brief description..." value={linkForm.description}
                        onChange={(e) => setLinkForm(prev => ({ ...prev, description: e.target.value }))} rows={2} />
                    </div>
                    <div className="space-y-2">
                      <Label>URL *</Label>
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-gray-400" />
                        <Input placeholder="https://..." value={linkForm.external_url}
                          onChange={(e) => setLinkForm(prev => ({ ...prev, external_url: e.target.value }))} data-testid="link-url-input" />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t mt-4 flex-shrink-0">
                <p className="text-sm text-gray-500">
                  {selectedOptionType === "profile" && `${selectedForAdd.length} selected`}
                  {selectedOptionType === "image_gallery" && `${imageGalleryForm.files.length} images`}
                  {selectedOptionType === "pdf" && (pdfForm.file ? "1 file" : "No file")}
                  {selectedOptionType === "external_link" && (linkForm.external_url ? "1 link" : "No link")}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowAddOptionModal(null)}>Cancel</Button>
                  <Button className="bg-blue-600 hover:bg-blue-700" disabled={uploading} data-testid="add-option-submit"
                    onClick={() => {
                      if (selectedOptionType === "profile") handleAddProfiles();
                      else if (selectedOptionType === "image_gallery") handleAddImageGallery();
                      else if (selectedOptionType === "pdf") handleAddPdf();
                      else if (selectedOptionType === "external_link") handleAddLink();
                    }}>
                    {uploading ? "Uploading..." : "Add"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Schedule Meeting Modal */}
      {showMeetingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Video className="w-5 h-5 text-blue-600" /> Schedule Meeting
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowMeetingModal(null)}><X className="w-4 h-4" /></Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">Scheduling for <strong>{getOptionDisplayName(showMeetingModal)}</strong></p>
              <div className="space-y-2">
                <Label>Meeting Type</Label>
                <select value={meetingForm.meeting_type} onChange={(e) => setMeetingForm(prev => ({ ...prev, meeting_type: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm">
                  {MEETING_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input type="date" value={meetingForm.meeting_date}
                    onChange={(e) => setMeetingForm(prev => ({ ...prev, meeting_date: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>Time</Label>
                  <Input type="time" value={meetingForm.meeting_time}
                    onChange={(e) => setMeetingForm(prev => ({ ...prev, meeting_time: e.target.value }))} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Meeting Link (optional)</Label>
                <div className="flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-gray-400" />
                  <Input type="url" placeholder="https://meet.google.com/..." value={meetingForm.meeting_link}
                    onChange={(e) => setMeetingForm(prev => ({ ...prev, meeting_link: e.target.value }))} />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowMeetingModal(null)}>Cancel</Button>
                <Button onClick={handleSaveMeeting} className="bg-blue-600 hover:bg-blue-700">
                  <Check className="w-4 h-4 mr-2" /> Save Meeting
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Share Link Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Share2 className="w-5 h-5 text-blue-600" /> Share Project
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowShareModal(false)}><X className="w-4 h-4" /></Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {project.share_link ? (
                <>
                  <p className="text-sm text-gray-600">Share this link with your client to view and select options.</p>
                  <div className="flex items-center gap-2">
                    <Input value={getProjectShareUrl(project.share_link.token)} readOnly
                      className="text-sm font-mono bg-gray-50" onClick={(e) => e.target.select()} />
                    <Button onClick={copyShareLink} size="sm" className="shrink-0" data-testid="copy-share-link-btn">
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">Created: {formatDate(project.share_link.created_at)}</p>
                  <Button variant="outline" className="w-full" onClick={handleCreateShareLink}>
                    <Link2 className="w-4 h-4 mr-2" /> Generate New Link
                  </Button>
                </>
              ) : (
                <div className="text-center py-4">
                  <Share2 className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm text-gray-600 mb-4">No share link yet</p>
                  <Button onClick={handleCreateShareLink} className="bg-blue-600 hover:bg-blue-700">Generate Share Link</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Project Modal */}
      {showEditProjectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg">Edit Project</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowEditProjectModal(false)}><X className="w-4 h-4" /></Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Project Title</Label>
                <Input value={editForm.title} onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={editForm.description} onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))} rows={3} />
              </div>
              <div className="space-y-2">
                <Label>Deadline</Label>
                <Input type="date" value={editForm.deadline} onChange={(e) => setEditForm(prev => ({ ...prev, deadline: e.target.value }))} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowEditProjectModal(false)}>Cancel</Button>
                <Button onClick={handleUpdateProject} className="bg-blue-600 hover:bg-blue-700">
                  <Check className="w-4 h-4 mr-2" /> Save
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
